import useAppDispatch from 'js/hooks/useAppDispatch';
import { useCallback } from 'react';
import { loadPipeline, refreshPipeline } from 'js/actions/pipelines';
import { restorePipeline } from 'new/actions/pipelines/archivalActions';
import {
  loadFlowsWithProjects,
  loadFlowsWithoutProjects,
  loadFlowsWithProjectsCount,
  loadFlowsWithoutProjectsCount,
  cleanupFlowsWithoutProjects,
} from 'js/actions/pipelines/flowsByProject';

type FlowResponse = {
  entities: {
    flowNodes: object;
  };
};

type Options = {
  page?: number;
  per_page?: number;
  sort?: { sortKey: string; sortOrder: string };
  agentic?: boolean;
};

interface FlowAdapterHookReturn {
  restoreFlowById: (flowId: number) => Promise<any>;
  requestFlowById: (flowId: number) => Promise<FlowResponse>;
  refreshFlowById: (flowId: number) => Promise<FlowResponse>;
  requestFlowsWithProjects: (options?: Options) => Promise<any>;
  requestFlowsWithoutProjects: (options?: Options) => Promise<any>;
  requestFlowsWithProjectsCount: (options?: Options) => Promise<any>;
  requestFlowsWithoutProjectsCount: (options?: Options) => Promise<any>;
  cleanupFlowsWithoutProjects: () => Promise<any>;
}

function useFlowAdapter(): FlowAdapterHookReturn {
  const dispatch = useAppDispatch();

  const requestFlowById = useCallback(
    async (flowId: number) => dispatch(loadPipeline(flowId)),
    [dispatch],
  );
  const refreshFlowById = useCallback(
    async (flowId: number) => dispatch(refreshPipeline(flowId)),
    [dispatch],
  );
  const restoreFlowById = useCallback(
    async (flowId: number) => dispatch(restorePipeline(flowId)),
    [dispatch],
  );

  const requestFlowsWithProjects = useCallback(
    async (options?: Options) => dispatch(loadFlowsWithProjects(options || {})),
    [dispatch],
  );

  const requestFlowsWithoutProjects = useCallback(
    async (options?: Options) => dispatch(loadFlowsWithoutProjects(options || {})),
    [dispatch],
  );

  const requestFlowsWithProjectsCount = useCallback(
    async (options?: Options) => dispatch(loadFlowsWithProjectsCount(options || {})),
    [dispatch],
  );

  const requestFlowsWithoutProjectsCount = useCallback(
    async (options?: Options) => dispatch(loadFlowsWithoutProjectsCount(options || {})),
    [dispatch],
  );

  const _cleanupFlowsWithoutProjects = useCallback(
    async () => dispatch(cleanupFlowsWithoutProjects()),
    [dispatch],
  );

  return {
    requestFlowById,
    refreshFlowById,
    requestFlowsWithProjects,
    requestFlowsWithoutProjects,
    requestFlowsWithProjectsCount,
    requestFlowsWithoutProjectsCount,
    restoreFlowById,
    cleanupFlowsWithoutProjects: _cleanupFlowsWithoutProjects,
  };
}

export { useFlowAdapter };
